import { Add, CloudDownload } from '@mui/icons-material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { enqueueSnackbar } from '~/utils/notifications';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { MAX_FILES } from '~/components/DicomLoad/LocalLoad/LocalFilesTab';
import { useRefuseWhileViewerOpening } from '~/hooks/useRefuseWhileViewerOpening';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { sessionInitStatusAtom } from '~/state/atoms/sessionAtoms';
import { dicomSourcesModalOpenState } from '~/state/atoms/viewerAtoms';
import AiAssistantButton from '../../AiAssistantButton';
import StepHeader from '../StepHeader';
import DatasetList from './DatasetList';

export default function SetupContent() {
    const setDicomModalOpen = useSetRecoilState(dicomSourcesModalOpenState);
    const { initializeEnvironment } = useViewportEnvironment();
    const { processImportedFiles } = useProcessDicomFiles();
    const refuseWhileOpening = useRefuseWhileViewerOpening();
    const folderInputRef = React.useRef<HTMLInputElement | null>(null);
    const { sessionId } = useParams<{ sessionId?: string }>();
    const sessionInitStatus = useRecoilValue(sessionInitStatusAtom);
    const isSessionMode = !!sessionId;

    const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(
        null
    );
    const addMenuOpen = Boolean(addMenuAnchor);
    const handleOpenAddMenu = useCallback(
        (e: React.MouseEvent<HTMLElement>) => setAddMenuAnchor(e.currentTarget),
        []
    );
    const handleCloseAddMenu = useCallback(() => setAddMenuAnchor(null), []);

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.target.files && e.target.files[0]) {
                const files = Array.from(e.target.files);
                e.target.value = '';
                if (refuseWhileOpening()) {
                    return;
                }
                if (files.length > MAX_FILES) {
                    enqueueSnackbar(
                        `Loading canceled: too many files selected. Limit is ${MAX_FILES}.`,
                        { variant: 'error' }
                    );
                    return;
                }
                const processedItems = await processImportedFiles(files);
                if (processedItems) {
                    initializeEnvironment(processedItems);
                }
            }
        },
        [refuseWhileOpening, processImportedFiles, initializeEnvironment]
    );

    const handleLoadFolder = useCallback(() => {
        folderInputRef.current?.click();
    }, []);

    const openDicomModal = useCallback(
        () => setDicomModalOpen(true),
        [setDicomModalOpen]
    );

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box flexShrink={0}>
                <StepHeader titleId="workflow.setup" />

                <Box
                    px={2}
                    my={1}
                    mb={1.5}
                    display="flex"
                    flexDirection="column"
                    gap={0.5}
                >
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleOpenAddMenu}
                        data-testid="addDataButton"
                        sx={{
                            textTransform: 'capitalize',
                            fontSize: 12,
                        }}
                    >
                        <Add
                            sx={{
                                position: 'absolute',
                                left: 8,
                                fontSize: 16,
                            }}
                        />
                        <FormattedMessage id="addData" />
                    </Button>
                    <Menu
                        anchorEl={addMenuAnchor}
                        open={addMenuOpen}
                        onClose={handleCloseAddMenu}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    width: addMenuAnchor?.offsetWidth ?? 'auto',
                                },
                            },
                        }}
                    >
                        <MenuItem
                            component="label"
                            htmlFor="loadDicomInput"
                            sx={{ fontSize: 13 }}
                        >
                            <ListItemIcon>
                                <FileUploadIcon />
                            </ListItemIcon>
                            <ListItemText>
                                <FormattedMessage id="loadFiles" />
                            </ListItemText>
                            <input
                                id="loadDicomInput"
                                data-testid="loadDicomInput"
                                onChange={(e) => {
                                    handleCloseAddMenu();
                                    handleFileChange(e);
                                }}
                                type="file"
                                hidden
                                multiple
                            />
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                handleCloseAddMenu();
                                handleLoadFolder();
                            }}
                            data-testid="loadFolderButton"
                            sx={{ fontSize: 13 }}
                        >
                            <ListItemIcon>
                                <FolderOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText>
                                <FormattedMessage id="loadFolder" />
                            </ListItemText>
                        </MenuItem>
                        {!isSessionMode && (
                            <MenuItem
                                onClick={() => {
                                    handleCloseAddMenu();
                                    openDicomModal();
                                }}
                                sx={{ fontSize: 13 }}
                            >
                                <ListItemIcon>
                                    <CloudDownload />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="loadFromDicom" />
                                </ListItemText>
                            </MenuItem>
                        )}
                    </Menu>
                    <input
                        ref={folderInputRef}
                        type="file"
                        hidden
                        multiple
                        // @ts-expect-error webkitdirectory is a non-standard but widely supported attribute
                        webkitdirectory="true"
                        onChange={handleFileChange}
                    />
                    <AiAssistantButton />
                </Box>

                <Divider />
            </Box>

            <Box flex={1} overflow="auto" minHeight={0}>
                {sessionInitStatus.isLoading ? (
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        px={2}
                        py={1}
                    >
                        <CircularProgress size={16} />
                        <Typography fontSize={12} color="text.secondary">
                            <FormattedMessage id="downloadingSession" />
                        </Typography>
                    </Box>
                ) : (
                    <DatasetList />
                )}
            </Box>
        </Box>
    );
}
